import React, {useContext, useMemo, useEffect, useState} from 'react';
import "./LearningCenter.scss"
import {PageTitle} from "../Components/PageTitle/PageTitle";
import {FeatureFlags, SignDocCategories} from "../Utils/Constants";
import {RoutingTabs} from "../Utils/RoutingTabs/RoutingTabs";
import {SignDocsListWithFilters} from "../Components/LearningManagement/SignDocsListWithFilters/SignDocsListWithFilters";
import {I18nReactContext} from "../contexts/I18nContext";
import {CompanyReactContext} from "../contexts/CompanyContext";
import {getUserTaskTypes} from "../Utils/TabFiltering/TabFilteringUtils";
import {get, isEmpty} from "lodash";
import {getAllSignDocTypes} from "../Components/SignDoc/SignDocLib";

const getAllTabs = ({i18n, availableCertificationTypes, availableContentTypes}) => [
    {
        label: i18n.translate('learningCenter.tabs.myCertifications'),
        subPath: 'myCertifications',
        element: <SignDocsListWithFilters
            categories={[SignDocCategories.CERTIFICATION]}
            showForceCompletion={true}
            showNonForceCompletion={false}
            key="myCertifications"
            types={getAllSignDocTypes()}
            filterableTypes={availableCertificationTypes}
            showFilters={{search: true, type: true}}
            isUserView={true}
        />,
        requiredFeatureFlags: [FeatureFlags.CERTIFICATION]
    },
    {
        label: i18n.translate('learningCenter.tabs.myContent'),
        subPath: 'myContent',
        element: <SignDocsListWithFilters
            categories={[SignDocCategories.FORCE_COMPLETION]}
            showForceCompletion={true}
            showNonForceCompletion={false}
            key="myContent"
            types={getAllSignDocTypes()}
            filterableTypes={availableContentTypes}
            showFilters={{search: true, type: true}}
            isUserView={true}
        />
    },
    {
        label: i18n.translate('learningCenter.tabs.archived'),
        subPath: 'archived',
        element: <SignDocsListWithFilters
            key="archived"
            types={getAllSignDocTypes()}
            categories={[SignDocCategories.FORCE_COMPLETION, SignDocCategories.CERTIFICATION]}
            showFilters={{search: true, type: true}}
            isArchivedView={true}
        />
    }
]

const permanentTabs = ['myContent']

const TAB_ORDER = {
    'myCertifications': 2,
    'myContent': 1,
    'archived': 3
};

export const LearningCenter = () => {
    const {i18n} = useContext(I18nReactContext)
    const companyContext = useContext(CompanyReactContext)
    const [userTaskTypesCertification, setUserTaskTypesCertification] = useState([])
    const [userTaskTypesContent, setUserTaskTypesContent] = useState([])
    const [isLoadingTaskTypes, setIsLoadingTaskTypes] = useState(true)

    // Load user task types when component mounts and refresh periodically
    useEffect(() => {
        const loadUserTaskTypes = async () => {
            if (isEmpty(companyContext.featureFlags)) {
                return
            }
            try {
                setIsLoadingTaskTypes(true)

                // Load types for Certifications only
                const certificationTypes = await getUserTaskTypes({
                    categories: [SignDocCategories.CERTIFICATION],
                    showForceCompletion: true,
                    showNonForceCompletion: false
                })
                setUserTaskTypesCertification(certificationTypes)

                // Load types for My Content (FORCE_COMPLETION only, excluding CERTIFICATION)
                const contentTypes = await getUserTaskTypes({
                    categories: [SignDocCategories.FORCE_COMPLETION],
                    showForceCompletion: true,
                    showNonForceCompletion: false
                })
                setUserTaskTypesContent(contentTypes)
            } catch (error) {
                console.error('Error loading user task types:', error)
                setUserTaskTypesCertification([])
                setUserTaskTypesContent([])
            } finally {
                setIsLoadingTaskTypes(false)
            }
        }

        loadUserTaskTypes()
    }, [companyContext.featureFlags])

    // Calculate available user types for filtering
    const availableCertificationTypes = useMemo(() => {
        return [...new Set(userTaskTypesCertification)];
    }, [userTaskTypesCertification]);

    const availableContentTypes = useMemo(() => {
        return [...new Set(userTaskTypesContent)];
    }, [userTaskTypesContent]);

    // Filter tabs based on user task types
    const filteredTabs = useMemo(() => {
        if (isLoadingTaskTypes) {
            return [] // Show no tabs while loading
        }

        const allTabs = getAllTabs({ i18n, availableCertificationTypes, availableContentTypes })

        // Consolidated tab structure: My Certifications, My Content, Archived
        const filtered = allTabs.filter(tab => {
            // Always show permanent tabs (My Content)
            if (permanentTabs.includes(tab.subPath)) {
                return true
            }

            // For Certifications tab, check feature flag
            if (tab.subPath === 'myCertifications') {
                const hasFeatureFlag = !tab.requiredFeatureFlags ||
                    tab.requiredFeatureFlags.every(flag => get(companyContext, `featureFlags.${flag}`, false));
                return hasFeatureFlag;
            }

            // For Archived tab, check feature flag
            if (tab.subPath === 'archived') {
                const hasFeatureFlag = !tab.requiredFeatureFlags ||
                    tab.requiredFeatureFlags.every(flag => get(companyContext, `featureFlags.${flag}`, false));
                return hasFeatureFlag;
            }

            return false;
        });

        // Sort tabs: My Certifications, My Content, Archived
        const sorted = filtered.slice().sort((a, b) => {
            return (TAB_ORDER[a.subPath] || 999) - (TAB_ORDER[b.subPath] || 999);
        });

        return sorted;
    }, [i18n, isLoadingTaskTypes, companyContext.featureFlags, availableCertificationTypes, availableContentTypes])

    return <div className="LearningCenter MenuPage">
        <PageTitle
            title={i18n.translate('learningCenter.title')}
            subTitle={i18n.translate('learningCenter.subTitle')}
            size="medium"
            subTitleBold=""
            customComponent={null}
            noLogo={false}
        />
        <div className="menuInnerPage">
            <div className="learningCenterTabWrapper">
                <RoutingTabs tabs={filteredTabs} baseRoute={'learningCenter'}/>
            </div>
        </div>

    </div>
}

